// PreventiApp · Sprint 3 · Etapa 7 — Página de Cursos (admin_colegio).
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { listCursos, createCurso, updateCurso, listUsuarios } from '../../data/supabaseAdmin';

const NIVELES = ['1° básico','2° básico','3° básico','4° básico','5° básico','6° básico','7° básico','8° básico','1° medio','2° medio','3° medio','4° medio'];

export default function AdminCursos() {
  const { profile } = useAuth();
  const colegio_id = profile?.colegio_id;
  const [cursos, setCursos] = useState([]);
  const [docentes, setDocentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({});
  const [error, setError] = useState('');

  const reload = async () => {
    setLoading(true); setError('');
    try {
      const [cs, us] = await Promise.all([
        listCursos({ colegio_id }),
        listUsuarios({ colegio_id }),
      ]);
      setCursos(cs);
      setDocentes(us.filter((u) => ['docente','orientador','encargado_convivencia'].includes(u.rol) && u.estado === 'activo'));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { if (colegio_id) reload(); }, [colegio_id]);

  const startCreate = () => {
    setCreating(true);
    setDraft({ colegio_id, anio: new Date().getFullYear(), nivel: '1° básico', letra: 'A', profesor_jefe_id: null, estado: 'activo' });
  };
  const cancel = () => { setCreating(false); setDraft({}); };
  const save = async () => {
    try {
      await createCurso({ ...draft, profesor_jefe_id: draft.profesor_jefe_id || null });
      cancel(); reload();
    } catch (e) { alert(`Error: ${e.message}`); }
  };
  const toggleEstado = async (c) => {
    try { await updateCurso(c.id, { estado: c.estado === 'activo' ? 'inactivo' : 'activo' }); reload(); }
    catch (e) { alert(`Error: ${e.message}`); }
  };

  const profMap = Object.fromEntries(docentes.map((d) => [d.id, d.nombre_completo]));

  return (
    <div className="p-8 max-w-5xl" data-testid="page-cursos">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0B1B5E]">Cursos</h1>
        </div>
        <button onClick={startCreate} data-testid="curso-btn-nuevo"
                className="px-4 py-2 rounded-xl bg-[#5346E6] hover:bg-[#0B1B5E] text-white text-sm font-bold">
          + Nuevo curso
        </button>
      </div>

      {error && <div className="mb-4 p-3 rounded-xl bg-[#FEE2E2] text-[#991B1B] text-sm" data-testid="curso-error">{error}</div>}

      {creating && (
        <div className="mb-5 p-5 rounded-2xl bg-white border border-[#5346E6]/30 shadow-sm" data-testid="curso-form">
          <p className="text-sm font-extrabold text-[#0B1B5E] mb-3">Alta de curso</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
            <label className="block">Año
              <input type="number" data-testid="curso-input-anio" value={draft.anio} onChange={(e) => setDraft({ ...draft, anio: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7]" />
            </label>
            <label className="block">Nivel
              <select data-testid="curso-input-nivel" value={draft.nivel} onChange={(e) => setDraft({ ...draft, nivel: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7]">
                {NIVELES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label className="block">Letra
              <input data-testid="curso-input-letra" value={draft.letra} onChange={(e) => setDraft({ ...draft, letra: e.target.value.toUpperCase() })} maxLength={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7]" />
            </label>
            <label className="block">Profesor jefe
              <select data-testid="curso-input-jefe" value={draft.profesor_jefe_id || ''} onChange={(e) => setDraft({ ...draft, profesor_jefe_id: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#DDE6F7]">
                <option value="">— Sin asignar —</option>
                {docentes.map((d) => <option key={d.id} value={d.id}>{d.nombre_completo}</option>)}
              </select>
            </label>
          </div>
          <div className="mt-4 flex gap-2">
            <button data-testid="curso-btn-save" onClick={save} className="px-4 py-2 rounded-xl bg-[#10B981] hover:bg-[#059669] text-white text-sm font-bold">Guardar</button>
            <button onClick={cancel} className="px-4 py-2 rounded-xl border border-[#DDE6F7] text-[#475472] text-sm font-bold">Cancelar</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5EAF5] rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="cursos-table">
          <thead className="bg-[#F4F7FE] text-[#0B1B5E] text-xs">
            <tr>
              <th className="px-4 py-3 text-left font-bold">Año</th>
              <th className="px-4 py-3 text-left font-bold">Nivel</th>
              <th className="px-4 py-3 text-left font-bold">Letra</th>
              <th className="px-4 py-3 text-left font-bold">Profesor jefe</th>
              <th className="px-4 py-3 text-left font-bold">Estado</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {cursos.map((c) => (
              <tr key={c.id} className="border-t border-[#E5EAF5]" data-testid={`curso-row-${c.anio}-${c.letra}`}>
                <td className="px-4 py-3">{c.anio}</td>
                <td className="px-4 py-3">{c.nivel}</td>
                <td className="px-4 py-3 font-bold">{c.letra}</td>
                <td className="px-4 py-3 text-xs text-[#475472]">{profMap[c.profesor_jefe_id] || '—'}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.estado === 'activo' ? 'bg-[#DCFCE7] text-[#166534]' : 'bg-[#F4F7FE] text-[#475472]'}`}>{c.estado}</span></td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => toggleEstado(c)} data-testid={`curso-toggle-${c.anio}-${c.letra}`} className="text-xs font-bold text-[#5346E6] hover:underline">
                    {c.estado === 'activo' ? 'Desactivar' : 'Activar'}
                  </button>
                </td>
              </tr>
            ))}
            {!loading && cursos.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[#475472]">Sin cursos aún.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
